import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { useLiveStatus } from '../contexts/LiveStatusContext';
import { videosService } from '../services/videosService';
import { fetchLectureData } from '../services/googleSheets';
import { metaTagsService } from '../services/metaTagsService';
import useVideoAnalytics from '../hooks/useVideoAnalytics';
import InlineVideoAnchor from '../components/Video/InlineVideoAnchor';
import { usePlayer } from '../contexts/PlayerContext';
import SummaryPanel from '../components/Video/SummaryPanel';
import '../styles/videoWatchPage.css';

/**
 * Video Watch Page - Individual video playback with details
 */
const VideoWatchPage = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, theme } = useConfig();
  const { hasLiveVideos } = useLiveStatus();
  
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [centerData, setCenterData] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [videoSeriesInfo, setVideoSeriesInfo] = useState(null);
  const [lectureData, setLectureData] = useState(null); // Lecture data for this video
  const [showNotes, setShowNotes] = useState(false);
  const [notesView, setNotesView] = useState('keyTakeaways'); // 'keyTakeaways' or 'detailed'

  // Use video analytics hook
  const { viewCount, likeCount, playCount, hasLiked, toggleLike, trackView, startPlayTracking, stopPlayTracking } = useVideoAnalytics(video?.youtube_video_id);
  const { isActive, mode, minimizeSource, restore, minimize } = usePlayer();

  const themeColor = config?.primaryColor || theme?.colors?.primary || '#1e4d8b';

  // Brief skeleton overlay to avoid any inline blank while overlay measures
  const [showInlineSkeleton, setShowInlineSkeleton] = useState(true);
  useEffect(() => {
    // Scroll to top when page loads (prevents landing mid-page)
    window.scrollTo(0, 0);
    const t = setTimeout(() => setShowInlineSkeleton(false), 200);
    return () => clearTimeout(t);
  }, []);

  // Detect if we landed on this page with an already-minimized player (navigation case)
  const wasMinimizedOnMount = useRef(false);
  useEffect(() => {
    // Consider ourselves "arrived minimized" if mode is mini at mount
    // but only auto-restore if the minimize wasn't explicitly user-triggered here
    wasMinimizedOnMount.current = (mode === 'mini') && (minimizeSource !== 'button' && minimizeSource !== 'drag');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-restore inline only when we navigated here with an existing mini player
  useEffect(() => {
    if (!wasMinimizedOnMount.current) return;
    const t = setTimeout(() => {
      restore();
      wasMinimizedOnMount.current = false;
    }, 0);
    return () => clearTimeout(t);
  }, [restore]);

  // Safety: if the player becomes active after mount and we're mini due to navigation, restore inline
  useEffect(() => {
    if (isActive && mode === 'mini' && (minimizeSource !== 'button' && minimizeSource !== 'drag')) {
      const t = setTimeout(() => restore(), 0);
      return () => clearTimeout(t);
    }
  }, [isActive, mode, minimizeSource, restore]);

  // Bridge global overlay events to analytics
  useEffect(() => {
    const handler = (e) => {
      const type = e?.detail?.type;
      if (type === 'play') startPlayTracking();
      else if (type === 'pause' || type === 'ended') stopPlayTracking();
    };
    window.addEventListener('penta-video-player', handler);
    return () => window.removeEventListener('penta-video-player', handler);
  }, [startPlayTracking, stopPlayTracking]);

  // Helper function to extract Google Doc ID from URL
  const extractGoogleDocId = (url) => {
    if (!url) return null;
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  // Load video data
  useEffect(() => {
    const loadVideo = async () => {
      if (!videoId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get center data from config
        let centerData = null;
        
        // First try to use center_id from config (preferred method)
        if (config?.center_id) {
          console.log('[VideoWatchPage] Using center_id from config:', config.center_id);
          centerData = {
            id: config.center_id,
            name: config.name || 'Center'
          };
        } else if (config?.domain) {
          // Fallback to domain lookup if no center_id in config
          const domain = config.domain;
          console.log('[VideoWatchPage] No center_id in config, falling back to domain lookup:', domain);
          const center = await videosService.getCenterIdByDomain(domain);
          if (center) {
            centerData = center;
          }
        }
        
        if (centerData) {
          setCenterData(centerData);
        }
        
        // Get video data
        const videoData = await videosService.getVideoById(videoId);
        if (videoData) {
          setVideo(videoData);
          
          // Also try to get series info from Google Sheets if available
          if (config?.google_sheet_id) {
            const metadata = await videosService.fetchVideoMetadataFromSheets(config.google_sheet_id);
            const videoMeta = metadata.find(m => m.youtube_video_id === videoId);
            if (videoMeta) {
              setVideoSeriesInfo({
                seriesName: videoMeta.series,
                eventName: videoMeta.eventName,
                speakerName: videoMeta.speakerName
              });
            }
            
            // Fetch lecture data to check for notes
            try {
              const lectures = await fetchLectureData(config.google_sheet_id);
              // Find lecture that matches this video
              const matchedLecture = lectures.find(lecture => {
                return (lecture.fullVideoUrl && lecture.fullVideoUrl.includes(videoId)) ||
                       (lecture.summaryVideoUrl && lecture.summaryVideoUrl.includes(videoId));
              });
              
              if (matchedLecture) {
                setLectureData(matchedLecture);
                console.log('[VideoWatchPage] Found matching lecture:', matchedLecture);
              }
            } catch (error) {
              console.error('[VideoWatchPage] Error fetching lecture data:', error);
            }
          }
          
          // Get related videos
          if (centerData) {
            const relatedResult = await videosService.fetchVideos(centerData.id, 'video', 0, 10);
            setRelatedVideos(relatedResult.videos.filter(v => v.id !== videoId));
          }
        } else {
          setError('Video not found');
        }
      } catch (error) {
        console.error('Error loading video:', error);
        setError('Failed to load video');
      } finally {
        setLoading(false);
      }
    };
    
    loadVideo();
  }, [videoId, config]);

  // Track view when video is loaded
  useEffect(() => {
    if (video && video.youtube_video_id) {
      trackView();
    }
  }, [video, trackView]);

  // Update meta tags when video data is loaded
  useEffect(() => {
    if (video) {
      // Build description with speaker and view count
      const speakerName = lectureData?.speaker || videoSeriesInfo?.speakerName || 'Penta University';
      const description = `Watch "${video.title}" by ${speakerName}. ${videosService.formatViews(video.youtube_view_count || viewCount)} views • ${videosService.formatPublishedDate(video.published_at)}`;
      
      // Get video thumbnail
      const videoId = video.youtube_video_id;
      const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
      
      // Update meta tags for social sharing
      metaTagsService.updateMetaTags({
        title: video.title,
        description: description,
        image: thumbnailUrl,
        url: window.location.href,
        type: 'video.other',
        video: {
          url: `https://www.youtube.com/watch?v=${videoId}`,
          embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null
        }
      });

      // Try maxresdefault first, fallback to hqdefault if it doesn't exist
      if (thumbnailUrl) {
        const img = new Image();
        img.onerror = () => {
          const fallbackThumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          metaTagsService.setMetaTag('og:image', fallbackThumbnail, 'property');
          metaTagsService.setMetaTag('twitter:image', fallbackThumbnail, 'name');
        };
        img.src = thumbnailUrl;
      }
    }
    
    // Cleanup: reset meta tags when component unmounts
    return () => {
      metaTagsService.resetToDefaults();
    };
  }, [video, viewCount, lectureData, videoSeriesInfo]);

  const handleVideoClose = () => {
    navigate('/videos');
  };

  const handleLike = () => {
    toggleLike();
  };

  const handleSubscribe = () => {
    // Open YouTube channel in new tab
    if (config?.youtube_channel_url) {
      window.open(config.youtube_channel_url, '_blank');
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: video?.title,
          text: `Watch "${video?.title}" on ${centerData?.name || config?.name || 'our platform'}`,
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        // Show toast notification
        const toast = document.createElement('div');
        toast.className = 'share-toast';
        toast.textContent = 'Link copied to clipboard!';
        toast.style.cssText = `
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          padding: 12px 24px;
          border-radius: 4px;
          z-index: 9999;
          animation: slideUp 0.3s ease-out;
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          toast.style.animation = 'slideDown 0.3s ease-out';
          setTimeout(() => document.body.removeChild(toast), 300);
        }, 2000);
      }
    } catch (error) {
      // Handle error silently - user likely cancelled the share
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  const handleDownload = () => {
    // TODO: Implement download functionality
    if (video?.video_url) {
      window.open(video.video_url, '_blank');
    }
  };

  const handleRelatedVideoClick = (relatedVideo) => {
    navigate(`/videos/${relatedVideo.id}`, { state: { autoplay: true } });
  };

  const handleGoToLecture = () => {
    if (lectureData?.lectureId) {
      navigate(`/lecture/${lectureData.lectureId}`, { state: { fromVideoId: videoId } });
    }
  };

  const handleReadNotes = () => {
    setShowNotes(!showNotes);
    // Hide description when showing notes
    if (!showNotes) {
      setShowDescription(false);
    }
  };

  const handleMoreFromSpeaker = () => {
    const speakerName = lectureData?.speaker || videoSeriesInfo?.speakerName;
    if (speakerName) {
      // Navigate to speaker page, matching shorts behavior
      navigate(`/speaker/${encodeURIComponent(speakerName)}`);
    }
  };

  if (loading) {
    return (
      <div className="video-watch-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-watch-error">
        <p>{error}</p>
        <button onClick={() => navigate('/videos')}>Back to Videos</button>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="video-watch-error">
        <p>Video not found</p>
        <button onClick={() => navigate('/videos')}>Back to Videos</button>
      </div>
    );
  }

  return (
    <div className="video-watch-page" style={{
      '--vwp-text': theme?.colors?.text || '#212121',
      '--vwp-text-secondary': theme?.colors?.cardTextSecondary || '#666666',
      '--vwp-bg': theme?.colors?.background || '#f5f5f5',
      '--vwp-card-bg': theme?.colors?.cardBg || '#ffffff',
      '--vwp-border': theme?.isDarkBackground ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
    }}>
      {/* Top Toggle: Videos | Live */}
      <div className="video-watch-toggle">
        <button
          className="toggle-button active"
          onClick={() => navigate('/videos')}
          style={{
            backgroundColor: themeColor,
            color: '#fff',
            border: 'none'
          }}
        >
          Videos
        </button>
        <button
          className={`toggle-button ${hasLiveVideos ? 'has-live' : ''}`}
          onClick={() => navigate('/videos', { state: { activeTab: 'live' } })}
          style={{
            backgroundColor: '#f8f9fa',
            color: '#666',
            border: '1px solid #e0e0e0'
          }}
        >
          Live
          {hasLiveVideos && <span className="live-dot-indicator" />}
        </button>
      </div>

      <div className="video-watch-container">
        {/* Main Video Section */}
        <div className="video-main-section">
          {/* Inline anchor for the persistent global player */}
          <div style={{ position: 'relative' }}>
            {/* Load immediately using route param, then swap to canonical id when video metadata arrives */}
            <InlineVideoAnchor
              videoId={video?.youtube_video_id || videoId}
              playOnLoad={location.state?.autoplay}
            />
            {showInlineSkeleton && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#111',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
          {/* Minimize button removed; Global overlay provides minimize/maximize/close controls */}
          
          {/* Video Info */}
          <div className="video-info-section">
            {/* Series Badge */}
            {videoSeriesInfo?.eventName && (
              <div className="video-series-badge" style={{ backgroundColor: themeColor }}>
                <span className="series-icon">📚</span>
                <span className="series-name">{videoSeriesInfo.eventName}</span>
              </div>
            )}
            <h1 className="video-title">{video.title}</h1>
            <div className="video-metadata">
              <span className="video-views">
                {videosService.formatViews(video.youtube_view_count || viewCount)} views
              </span>
              <span className="video-published">
                • {videosService.formatPublishedDate(video.published_at)}
              </span>
            </div>
            
            {/* Action Bar */}
            <div className="video-action-bar">
              <div className="action-buttons-left">
                <button 
                  className={`action-button ${hasLiked ? 'liked' : ''}`}
                  onClick={handleLike}
                  style={hasLiked ? { color: themeColor } : {}}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill={hasLiked ? themeColor : 'currentColor'}>
                    <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                  </svg>
                  <span className="action-label">
                    {likeCount > 0 ? likeCount : 'Like'}
                  </span>
                </button>
                
                <button className="action-button" onClick={handleShare}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  <span className="action-label">Share</span>
                </button>
                
                {/* Lecture action buttons - only show if lecture data exists */}
                {lectureData && (
                  <>
                    <button 
                      className="action-button"
                      onClick={handleGoToLecture}
                      style={{ color: themeColor }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill={themeColor}>
                        <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>
                      </svg>
                      <span className="action-label">Lecture</span>
                    </button>
                    
                    <button 
                      className="action-button"
                      onClick={handleReadNotes}
                      style={{ color: showNotes ? themeColor : undefined }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill={showNotes ? themeColor : 'currentColor'}>
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      </svg>
                      <span className="action-label">Summary</span>
                    </button>
                  </>
                )}
                
              </div>
            </div>
            
            {/* Lecture Summary — rendered inline from .docx via mammoth.js */}
            {showNotes && lectureData && (
              <SummaryPanel
                summaryDocId={lectureData.summaryDocId}
                themeColor={themeColor}
                onClose={() => setShowNotes(false)}
              />
            )}
            
            {/* Channel Info */}
            <div className="channel-info">
              <div className="channel-left">
                <div className="channel-avatar">
                  {config?.logo_url ? (
                    <img 
                      src={config.logo_url} 
                      alt={centerData?.name || 'Center'}
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill={themeColor}>
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  )}
                </div>
                <div className="channel-details">
                  {(lectureData?.speaker || videoSeriesInfo?.speakerName) ? (
                    <div 
                      className="speaker-info-inline"
                      onClick={handleMoreFromSpeaker}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleMoreFromSpeaker()}
                    >
                      <h3 className="speaker-name">{lectureData?.speaker || videoSeriesInfo?.speakerName}</h3>
                      <p className="speaker-subtitle">
                        More from this speaker 
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
                          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                        </svg>
                      </p>
                    </div>
                  ) : (
                    <h3 className="channel-name">{`@${centerData?.name || 'Channel'}`}</h3>
                  )}
                </div>
              </div>
              <button 
                className="subscribe-button"
                onClick={handleSubscribe}
                style={{ 
                  backgroundColor: '#ff0000', 
                  color: 'white' 
                }}
              >
                Subscribe to YouTube Channel
              </button>
            </div>
            
            {/* Video Description */}
            {!showNotes && (
              <div className="video-description">
                <button 
                  className="description-toggle"
                  onClick={() => setShowDescription(!showDescription)}
                >
                  {showDescription ? 'Show less' : 'Show more'}
                </button>
                {showDescription && (
                  <div className="description-content">
                    <p>Video description would go here...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Related Videos Sidebar */}
        <div className="related-videos-section">
          <h3 className="related-videos-title">Related Videos</h3>
          <div className="related-videos-list">
            {relatedVideos.map((relatedVideo) => (
              <div 
                key={relatedVideo.id}
                className="related-video-card"
                onClick={() => handleRelatedVideoClick(relatedVideo)}
              >
                <div className="related-video-thumbnail">
                  <img src={relatedVideo.thumbnail} alt={relatedVideo.title} />
                  <span className="related-video-duration">{relatedVideo.duration}</span>
                </div>
                <div className="related-video-info">
                  <h4 className="related-video-title">{relatedVideo.title}</h4>
                  <p className="related-video-metadata">
                    <span className="related-video-channel">@{centerData?.name}</span>
                    <span className="related-video-views">{relatedVideo.views} views</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoWatchPage;